import axios from 'axios';
import { API_VERSION_PREFIX } from './routes';
import { apiRoutes } from './routes';
import {
  clearTokensIfCurrent,
  getAccessToken,
  getSessionSnapshot,
  replaceTokensIfCurrent,
  type SessionSnapshot,
  type StoredTokens,
} from './sessionTokens';
import {
  apiRateLimitCooldown,
  rateLimitPartition,
  retryAfterMilliseconds,
} from './rateLimitCooldown';

declare module 'axios' {
  interface AxiosRequestConfig {
    helixAuth?: 'portal' | 'connector';
    _helixSessionGeneration?: string | null;
  }

  interface InternalAxiosRequestConfig {
    helixAuth?: 'portal' | 'connector';
    _helixRetried?: boolean;
    _helixAccessToken?: string | null;
  }
}

export interface ApiError {
  code: string;
  message: string;
  status: number;
  correlationId?: string;
  retryable: boolean;
  retryAfterMs?: number;
}

export class NormalizedApiError extends Error implements ApiError {
  readonly code: string;
  readonly status: number;
  readonly correlationId?: string;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'NormalizedApiError';
    this.code = error.code;
    this.status = error.status;
    this.retryable = error.retryable;
    if (error.correlationId !== undefined) this.correlationId = error.correlationId;
    if (error.retryAfterMs !== undefined) this.retryAfterMs = error.retryAfterMs;
  }
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? API_VERSION_PREFIX,
  timeout: 60_000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

let refreshPromise: { generation: string; promise: Promise<string | null> } | null = null;

export async function getValidAccessToken(): Promise<string | null> {
  const session = getSessionSnapshot();
  if (!session) return null;
  return shouldRefreshBeforeRequest(session) ? refreshAccessToken(session) : session.accessToken;
}

apiClient.interceptors.request.use(async (config) => {
  if (config.signal?.aborted) throw new axios.CanceledError('canceled', config);
  config.headers.set('X-Correlation-ID', crypto.randomUUID());
  config.headers.set('Accept-Language', document.documentElement.lang || 'en');
  let accessToken = getAccessToken();
  if (usesPortalQuota(config.url, config.helixAuth)) {
    const cooldown = apiRateLimitCooldown.remaining(rateLimitPartition(accessToken));
    if (cooldown > 0)
      throw new NormalizedApiError({
        code: 'http_429',
        status: 429,
        message: 'Too many requests. Please try again shortly.',
        retryable: true,
        retryAfterMs: cooldown,
      });
  }
  if (usesPortalSession(config.url, config.helixAuth)) {
    const session = getSessionSnapshot();
    accessToken = session?.accessToken ?? null;
    const generation = session?.generation ?? null;
    if (
      config._helixSessionGeneration !== undefined &&
      config._helixSessionGeneration !== generation
    )
      throw new axios.CanceledError('Session changed', config);
    config._helixSessionGeneration = generation;
    if (session && shouldRefreshBeforeRequest(session))
      accessToken = await refreshAccessToken(session);
    if (config.signal?.aborted) throw new axios.CanceledError('canceled', config);
    if (getSessionSnapshot()?.generation !== session?.generation)
      throw new axios.CanceledError('Session changed', config);
    config._helixAccessToken = accessToken;
    if (accessToken !== null) config.headers.set('Authorization', `Bearer ${accessToken}`);
    else config.headers.delete('Authorization');
  }
  return config;
});

function usesPortalQuota(url: string | undefined, auth: 'portal' | 'connector' | undefined) {
  if (auth === 'connector') return false;
  const path = new URL(url ?? '', window.location.origin).pathname;
  return !/(?:^|\/)auth(?:\/|$)/.test(path);
}

function usesPortalSession(url: string | undefined, auth: 'portal' | 'connector' | undefined) {
  if (auth === 'connector') return false;
  const path = new URL(url ?? '', window.location.origin).pathname;
  return ![
    apiRoutes.auth.login,
    apiRoutes.auth.verifyOtp,
    apiRoutes.auth.resendOtp,
    apiRoutes.auth.refresh,
    apiRoutes.auth.forgotPassword,
    apiRoutes.auth.resetPassword,
  ].some((route) => path.endsWith(route));
}

function shouldRefreshBeforeRequest(session: SessionSnapshot): boolean {
  if (session.refreshToken === null) return false;
  if (session.accessToken === null) return true;
  const expiresAt = getJwtExpiryTime(session.accessToken);
  if (expiresAt === null) return false;
  return expiresAt - Date.now() <= 30_000;
}

function expireSession(expected: SessionSnapshot) {
  if (clearTokensIfCurrent(expected))
    window.dispatchEvent(new CustomEvent('helix:session-expired'));
}

function sessionChanged(): never {
  throw new axios.CanceledError('Session changed');
}

function refreshAccessToken(expected: SessionSnapshot): Promise<string | null> {
  if (refreshPromise?.generation === expected.generation) return refreshPromise.promise;
  const pending = {
    generation: expected.generation,
    promise: Promise.resolve<string | null>(null),
  };
  pending.promise = withRefreshLock(() => refreshUnderLock(expected)).finally(() => {
    if (refreshPromise === pending) refreshPromise = null;
  });
  refreshPromise = pending;
  return pending.promise;
}

async function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  // Web Locks serialize rotating refresh tokens across all tabs on this origin.
  // The per-tab promise + storage CAS remain protective on older browsers.
  if (!navigator.locks?.request) return work();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    return await navigator.locks.request(
      'helix-session-refresh',
      { signal: controller.signal },
      work,
    );
  } catch (error) {
    if (controller.signal.aborted)
      throw new NormalizedApiError({
        code: 'session_refresh_busy',
        message: 'Session renewal is busy. Please retry.',
        status: 0,
        retryable: true,
      });
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

async function refreshUnderLock(expected: SessionSnapshot): Promise<string | null> {
  const current = getSessionSnapshot();
  if (!current || current.generation !== expected.generation) return sessionChanged();
  if (
    current.refreshToken !== expected.refreshToken ||
    current.accessToken !== expected.accessToken
  )
    return current.accessToken;
  if (!current.refreshToken) {
    expireSession(current);
    return null;
  }
  try {
    const response = await axios.post<{ isSuccess: boolean; data: StoredTokens | null }>(
      `${apiClient.defaults.baseURL ?? API_VERSION_PREFIX}${apiRoutes.auth.refresh}`,
      { refreshToken: current.refreshToken },
      { timeout: 10_000, withCredentials: true, headers: { Accept: 'application/json' } },
    );
    const tokens = response.data.data;
    if (
      !response.data.isSuccess ||
      !tokens ||
      typeof tokens.accessToken !== 'string' ||
      !tokens.accessToken ||
      typeof tokens.refreshToken !== 'string' ||
      !tokens.refreshToken
    )
      throw new NormalizedApiError({
        code: 'session_refresh_unavailable',
        message: 'Session renewal is temporarily unavailable. Please retry.',
        status: 502,
        retryable: true,
      });
    if (replaceTokensIfCurrent(current, tokens)) return tokens.accessToken;
    const latest = getSessionSnapshot();
    if (latest?.generation === current.generation) return latest.accessToken;
    return sessionChanged();
  } catch (error) {
    if (axios.isCancel(error) || error instanceof NormalizedApiError) throw error;
    const latest = getSessionSnapshot();
    if (latest?.generation !== current.generation) return sessionChanged();
    if (latest.refreshToken !== current.refreshToken || latest.accessToken !== current.accessToken)
      return latest.accessToken;
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      expireSession(current);
      return null;
    }
    // A timeout, offline browser, throttling, proxy or server error is NOT a logout.
    const status = axios.isAxiosError(error) ? (error.response?.status ?? 0) : 0;
    throw new NormalizedApiError({
      code: 'session_refresh_unavailable',
      message: 'Session renewal is temporarily unavailable. Please retry.',
      status,
      retryable: status === 0 || status === 429 || status >= 500,
      ...(status === 429 && axios.isAxiosError(error)
        ? { retryAfterMs: retryAfterMilliseconds(error.response?.headers['retry-after']) }
        : {}),
    });
  }
}

function getJwtExpiryTime(token: string): number | null {
  const [, payload] = token.split('.');
  if (payload === undefined) return null;
  try {
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const json = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {
      exp?: unknown;
    };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => {
    const config = response.config;
    if (
      usesPortalSession(config.url, config.helixAuth) &&
      config._helixSessionGeneration !== undefined &&
      config._helixSessionGeneration !== (getSessionSnapshot()?.generation ?? null)
    )
      throw new axios.CanceledError('Session changed', config);
    return response;
  },
  async (error: unknown) => {
    if (axios.isCancel(error)) throw error;
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      const original = error.config;
      if (
        status === 401 &&
        original !== undefined &&
        usesPortalSession(original.url, original.helixAuth)
      ) {
        const session = getSessionSnapshot();
        // A late response from a prior login must never refresh/clear the new one.
        if (session && session.generation === original._helixSessionGeneration) {
          if (original._helixRetried !== true) {
            original._helixRetried = true;
            const accessToken =
              session.accessToken !== original._helixAccessToken
                ? session.accessToken
                : await refreshAccessToken(session);
            if (accessToken !== null) {
              original.headers.set('Authorization', `Bearer ${accessToken}`);
              return apiClient.request(original);
            }
          } else if (session.accessToken === original._helixAccessToken) {
            expireSession(session);
          }
        }
      }
      const correlationId = error.response?.headers['x-correlation-id'] as string | undefined;
      const authorization = original?.headers.get('Authorization');
      const responseToken =
        typeof authorization === 'string' && authorization.startsWith('Bearer ')
          ? authorization.slice(7)
          : getAccessToken();
      const retryAfterMs =
        status === 429 && usesPortalQuota(original?.url, original?.helixAuth)
          ? apiRateLimitCooldown.record(
              rateLimitPartition(responseToken),
              error.response?.headers['retry-after'],
            )
          : undefined;
      const responseBody = error.response?.data as
        { message?: unknown; detail?: unknown; errorCode?: unknown } | undefined;
      const serverMessage =
        typeof responseBody?.message === 'string'
          ? responseBody.message
          : typeof responseBody?.detail === 'string'
            ? responseBody.detail
            : error.message;
      const normalized: ApiError = {
        code:
          typeof responseBody?.errorCode === 'string'
            ? responseBody.errorCode
            : status === 0
              ? 'network_error'
              : `http_${String(status)}`,
        message: serverMessage,
        status,
        retryable: status === 0 || status === 429 || status >= 500,
        ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
        ...(correlationId === undefined ? {} : { correlationId }),
      };
      throw new NormalizedApiError(normalized);
    }
    throw error instanceof Error ? error : new Error('Unknown API error');
  },
);
